<template lang="pug">
  .support-kb-editor(v-loading="loading")
    .support-page-header
      el-button(@click="goBack" size="small") ← Volver
      h1 {{ isNew ? 'Nuevo artículo' : 'Editar artículo' }}
      .support-page-actions
        el-button(size="small" @click="togglePreview") {{ showPreview ? 'Editar' : 'Vista previa' }}
        el-button(type="primary" size="small" :loading="saving" @click="save") 💾 Guardar
        el-button(v-if="!isNew && hasSource" size="small" @click="goToDiff") ⇄ Comparar con .md
        el-button(v-if="!isNew" size="small" type="danger" @click="archive") Archivar

    el-row(:gutter="16")
      el-col(:span="16")
        .support-editor-pane
          el-input(v-model="form.titulo" placeholder="Título del artículo" size="medium" style="margin-bottom: 12px")
          el-input(
            v-if="!showPreview"
            type="textarea"
            v-model="form.contenidoMarkdown"
            :rows="20"
            placeholder="Contenido en markdown..."
            resize="none"
          )
          .support-md-preview(v-else v-html="renderedMarkdown")

      el-col(:span="8")
        .support-editor-meta
          h3 Metadata
          el-form(label-width="100px" label-position="top" size="small")
            el-form-item(label="Módulo")
              el-input(v-model="form.modulo")
            el-form-item(label="Audiencia")
              el-checkbox-group(v-model="form.audiencia")
                el-checkbox(label="medico") médico
                el-checkbox(label="secretaria") secretaria
                el-checkbox(label="admin") admin
                el-checkbox(label="paciente") paciente
            el-form-item(label="Scope")
              el-radio-group(v-model="form.scope")
                el-radio(label="global") Global
                el-radio(label="tenant") Solo este tenant
            el-form-item(label="Estado")
              el-select(v-model="form.status")
                el-option(label="Publicado" value="published")
                el-option(label="Draft" value="draft")
                el-option(label="Archivado" value="archived")
            el-form-item(label="Variantes de pregunta")
              .support-variants
                .support-variant(v-for="(v, i) in form.variantes" :key="i")
                  el-input(v-model="form.variantes[i]" size="small" placeholder="otra forma de preguntar")
                  i.el-icon-close(@click="form.variantes.splice(i, 1)")
                el-button(size="mini" @click="form.variantes.push('')") + Agregar variante

          h3(v-if="hasSource" style="margin-top: 16px") Origen
          .support-source-block(v-if="hasSource")
            div 📄 {{ form.source.file }}
            div Sección: "{{ form.source.section }}"
            div(v-if="drift && drift.status === 'drifted'")
              el-tag(size="mini" type="warning") drift detectado
              span(style="margin-left: 8px") el .md cambió desde el snapshot
            div(v-else-if="drift && drift.status === 'synced'")
              el-tag(size="mini" type="success") sincronizado
            el-button(size="mini" @click="goToDiff") 👁 Ver original
            el-button(size="mini" @click="restore" :loading="restoring") ↺ Restaurar
</template>

<script>
import supportService from "@/services/support";

/**
 * @component KBEditor
 * @description Editor de artículos KB (Fase 2 — UC-REG-098).
 *              Soporta crear (ruta /support/kb/new) y editar
 *              (/support/kb/:id). Markdown con preview, metadata,
 *              variantes de pregunta, restaurar desde .md original.
 * @example <KBEditor />
 */
export default {
  name: "KBEditor",
  data() {
    return {
      loading: false,
      saving: false,
      restoring: false,
      showPreview: false,
      form: {
        titulo: "",
        contenidoMarkdown: "",
        modulo: "",
        audiencia: ["medico"],
        scope: "global",
        status: "published",
        variantes: [],
        source: null,
      },
      drift: null,
    };
  },
  computed: {
    /**
     * @description Ruta es 'support-kb-new' → modo creación.
     * @returns {boolean}
     */
    isNew() {
      return this.$route.name === "support-kb-new";
    },
    /**
     * @description Tiene origen .md (vs artículo manual).
     * @returns {boolean}
     */
    hasSource() {
      return !!(this.form.source && this.form.source.file);
    },
    /**
     * @description Render simple del markdown — convierte ## ## y newlines.
     *              Para un render más rico habría que sumar lib como marked.
     * @returns {string}
     */
    renderedMarkdown() {
      const md = this.form.contenidoMarkdown || "";
      return md
        .replace(/^### (.+)$/gm, "<h3>$1</h3>")
        .replace(/^## (.+)$/gm, "<h2>$1</h2>")
        .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
        .replace(/\*(.+?)\*/g, "<em>$1</em>")
        .replace(/`([^`]+)`/g, "<code>$1</code>")
        .replace(/\n\n/g, "</p><p>")
        .replace(/^/, "<p>")
        .concat("</p>");
    },
  },
  mounted() {
    if (!this.isNew) {
      this.load();
    }
  },
  methods: {
    /**
     * @description Carga el artículo desde el backend.
     */
    async load() {
      this.loading = true;
      try {
        const article = await supportService.getArticle(this.$route.params.id);
        this.form = {
          titulo: article.titulo || "",
          contenidoMarkdown: article.contenidoMarkdown || "",
          modulo: article.modulo || "",
          audiencia: article.audiencia || [],
          scope: article.scope || "global",
          status: article.status || "published",
          variantes: article.variantes || [],
          source: article.source || null,
        };
        if (this.hasSource) {
          const sourceData = await supportService.getArticleSource(
            this.$route.params.id
          );
          this.drift = sourceData.drift;
        }
      } catch (err) {
        this.$message.error("No pude cargar el artículo.");
      } finally {
        this.loading = false;
      }
    },
    /**
     * @description Guarda (crea o actualiza).
     */
    async save() {
      this.saving = true;
      try {
        const payload = { ...this.form };
        // Si scope=global, organization debe ser null.
        if (payload.scope === "global") {
          payload.organization = null;
        }
        let saved;
        if (this.isNew) {
          saved = await supportService.createArticle(payload);
          this.$message.success("Artículo creado.");
          this.$router.replace({
            name: "support-kb-edit",
            params: { id: saved._id },
          });
        } else {
          saved = await supportService.updateArticle(
            this.$route.params.id,
            payload
          );
          this.$message.success("Cambios guardados.");
        }
      } catch (err) {
        const msg =
          (err.response && err.response.data && err.response.data.message) ||
          "No pude guardar el artículo.";
        this.$message.error(msg);
      } finally {
        this.saving = false;
      }
    },
    /**
     * @description Soft delete con confirmación.
     */
    async archive() {
      try {
        await this.$confirm(
          "¿Archivar este artículo? El bot dejará de usarlo.",
          "Confirmar",
          {
            confirmButtonText: "Archivar",
            cancelButtonText: "Cancelar",
            type: "warning",
          }
        );
      } catch (e) {
        return;
      }
      try {
        await supportService.deleteArticle(this.$route.params.id);
        this.$message.success("Artículo archivado.");
        this.$router.push({ name: "support-kb-list" });
      } catch (err) {
        this.$message.error("No pude archivar.");
      }
    },
    /**
     * @description Restaurar contenido al snapshot del .md.
     */
    async restore() {
      try {
        await this.$confirm(
          "Esto pisa el contenido actual con el original del .md. ¿Seguir?",
          "Confirmar",
          {
            confirmButtonText: "Restaurar",
            cancelButtonText: "Cancelar",
          }
        );
      } catch (e) {
        return;
      }
      this.restoring = true;
      try {
        const updated = await supportService.restoreArticle(
          this.$route.params.id
        );
        this.form.contenidoMarkdown = updated.contenidoMarkdown;
        this.$message.success("Restaurado al original.");
        await this.load();
      } catch (err) {
        this.$message.error("No pude restaurar.");
      } finally {
        this.restoring = false;
      }
    },
    togglePreview() {
      this.showPreview = !this.showPreview;
    },
    goToDiff() {
      this.$router.push({
        name: "support-kb-diff",
        params: { id: this.$route.params.id },
      });
    },
    goBack() {
      this.$router.push({ name: "support-kb-list" });
    },
  },
};
</script>

<style lang="scss" scoped>
.support-page-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  h1 {
    margin: 0;
    font-size: 22px;
    color: #002d5e;
    flex: 1;
  }
}
.support-page-actions {
  display: flex;
  gap: 8px;
}
.support-editor-pane {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
}
.support-md-preview {
  min-height: 400px;
  padding: 16px;
  background: #f7f9fb;
  border-radius: 4px;
}
.support-editor-meta {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  h3 {
    margin: 0 0 12px 0;
    font-size: 14px;
    color: #002d5e;
  }
}
.support-variants {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.support-variant {
  display: flex;
  align-items: center;
  gap: 6px;
  i {
    cursor: pointer;
    color: #999;
  }
}
.support-source-block {
  font-size: 12px;
  color: #6b7294;
  display: flex;
  flex-direction: column;
  gap: 6px;
  div {
    word-break: break-all;
  }
}
</style>
